import { useCallback, useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { NBXAccounts } from 'services';
import { formatAndTruncate } from 'helpers/assets';
import { formatCreatedAt } from 'helpers/dates';
import { isNullish } from 'helpers/objects';
import Big from 'big.js';
import { isOpenOrder, isClosedOrder, getFillsQuantitySum } from 'helpers/orders';

Big.RM = 0;

function arraysEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // eslint-disable-line

  return isMobile;
}

export function useInterval(callback, delay) {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
    return () => {};
  }, [delay]);
}

export function useOpenOrders(accountId, asset, refreshRate = 30000) {
  const fetchOrders = async nextPageUrl => ({ results: NBXAccounts.getOrders(accountId, nextPageUrl) });

  const processOrders = ({ results }) => {
    let openOrders = results.body.filter(o => isOpenOrder(o.events));
    if (asset) {
      openOrders = openOrders.filter(order => {
        if (asset === 'All') return true;
        return order.market.includes(asset);
      });
    }
    return openOrders;
  };

  return usePagedHook({
    fetchContent: fetchOrders,
    processContent: processOrders,
    args: [accountId, asset],
    vetoCondition: isNullish(accountId),
    refreshRate,
    keyField: 'id',
    initialState: [],
    rowsCanChange: true,
    pauseAfterEmptyPages: 2
  });
}

export function useClosedOrders(accountId, asset, refreshRate = 30000) {
  const fetchOrders = async nextPageUrl => ({ results: NBXAccounts.getOrders(accountId, nextPageUrl) });

  const processOrders = ({ results }) => {
    let closedOrders = results.body.filter(o => isClosedOrder(o.events));
    if (asset) {
      closedOrders = closedOrders.filter(order => {
        if (asset === 'All') return true;
        return order.market.includes(asset);
      });
    }
    return closedOrders;
  };

  return usePagedHook({
    fetchContent: fetchOrders,
    processContent: processOrders,
    args: [accountId, asset],
    vetoCondition: isNullish(accountId),
    refreshRate,
    keyField: 'id',
    initialState: [],
    pauseAfterEmptyPages: 2
  });
}

export function useOrder(accountId, orderId, refreshRate = 30000) {
  const fetchOrder = async () => ({ results: NBXAccounts.getOrder(accountId, orderId) });

  const processOrder = ({ results }) => results;

  return useHook({
    fetchContent: fetchOrder,
    processContent: processOrder,
    args: [accountId, orderId],
    initialState: {},
    refreshRate
  });
}

export function useFills(accountId, asset, from, to, marketsHook, assetsHook) {
  // Keep track of relevant args so we abort the long request chains if they change
  const argsRef = useRef({ accountId, asset, from, to });

  useEffect(() => {
    argsRef.current = { accountId, asset, from, to };
  }, [accountId, asset, from, to]);

  const fetchOrdersForFills = async nextPageUrl => {
    const response = await NBXAccounts.getOrders(accountId, nextPageUrl);
    const results = { fills: [], nextPageUrl: response.nextPageUrl };
    let orders = response.body;
    // Filter out orders that cannot have fills within requested parameters
    const toAsDayJS = dayjs(to);
    orders = orders.filter(order => {
      if (getFillsQuantitySum(order) === 0) return false;
      if (asset !== 'All' && !order.market.includes(asset)) return false;
      if (to && dayjs(order.events.createdAt) > toAsDayJS) return false;
      return true;
    });

    orders.forEach(order => {
      const mFills = order.fills.map((fill, index) => ({
        ...fill,
        market: order.market,
        orderId: order.id,
        side: order.side,
        key: `${order.id}-${index}`,
        orderType: order.execution.type
      }));
      results.fills = results.fills.concat(mFills);
    });
    return { results, markets: marketsHook.data, assets: assetsHook.data };
  };

  const processFills = ({ results, markets, assets }) => {
    let { fills } = results;
    if (from || to) {
      const fromAsDayJS = dayjs(from);
      const toAsDayJS = dayjs(to);
      fills = fills.filter(fill => {
        if (to && dayjs(fill.createdAt) > toAsDayJS) return false;
        if (from && dayjs(fill.createdAt) < fromAsDayJS) return false;
        return true;
      });
    }

    fills.forEach(fill => {
      const market = markets.find(marketInMarkets => marketInMarkets.id === fill.market);

      const [base, quote] = fill.market.split('-');
      const total = Big(fill.price).times(fill.quantity);

      fill.settledTotal = `${formatAndTruncate(
        total,
        assets[market.quoteCurrency].displayPrecision
      )} ${quote}`;
      fill.settledFee = `${fill.fee} ${quote}`;

      fill.settledPrice = `${formatAndTruncate(fill.price, market.minTickLength)} ${quote}`;

      fill.settledQty = `${formatAndTruncate(
        fill.quantity,
        assets[market.baseCurrency].displayPrecision
      )} ${base}`;

      fill.filledAt = formatCreatedAt(fill.createdAt);
      fill.pair = fill.market.replace('-', '/');
    });
    return fills;
  };

  return usePagedHook({
    fetchContent: fetchOrdersForFills,
    processContent: processFills,
    args: [
      accountId,
      asset,
      from,
      to,
      marketsHook.loading,
      marketsHook.error,
      assetsHook.loading,
      assetsHook.error
    ],
    vetoCondition: marketsHook.loading || marketsHook.error || assetsHook.loading || assetsHook.error,
    initialState: []
  });
}

// Guardrails to make sure we don't set state when the data hook's parent is unmounted
// Or the request is otherwise cancelled
function guardrail(setStateFunction, isCancelled) {
  if (isCancelled) return;
  setStateFunction();
}

function useHook({
  fetchContent,
  processContent,
  args = [],
  refreshRate = null,
  initialState = [],
  vetoCondition = false,
  allOrNothing = false
}) {
  const REFRESH_RATE = useRef(refreshRate || null);
  const refreshInterval = useRef();
  const didCancel = useRef(false);
  const argsRef = useRef(args);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(initialState);

  const doFetch = useCallback(async () => {
    try {
      if (vetoCondition) {
        guardrail(() => {
          setData(initialState);
          setLoading(false);
        }, didCancel.current);
        return;
      }
      const argsAtStart = argsRef.current;
      // fetchContent must return either a Promise or an array of Promises
      const returnedObject = await fetchContent();
      let resolved;
      // If an array of Promises, wait for them all and process results
      if (Array.isArray(returnedObject.results) && returnedObject.results.length > 0) {
        const settledPromises = await Promise.allSettled(returnedObject.results);
        let anyFulfilled = false;
        let allFulfilled = true;
        resolved = settledPromises.map(settledPromise => {
          if (settledPromise.status === 'rejected') {
            allFulfilled = false;
            return { data: null, error: settledPromise.status, reason: settledPromise.reason };
          }
          anyFulfilled = true;
          return { data: settledPromise.value, error: false };
        });
        // If none of the requests succeed, error out the whole thing
        // Or if the request must be completely succesful, but isn't
        if (!anyFulfilled || (!allFulfilled && allOrNothing)) {
          console.error('failover condition met on data hook', resolved); // eslint-disable-line
          guardrail(() => {
            setError(true);
            setLoading(false);
          }, didCancel.current);
          clearInterval(refreshInterval.current);
          return;
        }
      } else {
        // If a singular promise, just wait for it to resolve
        resolved = await returnedObject.results;
      }
      const fetched = { ...returnedObject, results: resolved };
      const processed = await processContent(fetched);
      // If the args have changed since the function was called, abandon the update
      // TODO: is there a more efficient way to do this?
      if (!arraysEqual(argsAtStart, argsRef.current)) {
        return;
      }
      guardrail(() => {
        setData(processed);
        setLoading(false);
      }, didCancel.current);
    } catch (caughtError) {
      console.error(caughtError); // eslint-disable-line
      guardrail(() => {
        setError(true);
        setLoading(false);
      }, didCancel.current);
      clearInterval(refreshInterval.current);
    }
  }, [fetchContent, processContent, vetoCondition, allOrNothing, initialState]);

  useEffect(() => {
    REFRESH_RATE.current = refreshRate;
  }, [refreshRate]);

  useEffect(
    () => {
      didCancel.current = false;
      argsRef.current = args;
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
      setError(false);
      setLoading(true);
      doFetch();
      if (REFRESH_RATE.current) {
        refreshInterval.current = setInterval(doFetch, REFRESH_RATE.current);
      }
      return () => {
        didCancel.current = true;
        clearInterval(refreshInterval.current);
      };
    },
    [...args, REFRESH_RATE] // eslint-disable-line
  );

  const refetch = (setLoadingOnRefetch = true) => {
    didCancel.current = false;
    setError(false);
    if (setLoadingOnRefetch) setLoading(true);
    doFetch();
    if (refreshInterval.current) clearInterval(refreshInterval.current);
    if (REFRESH_RATE.current) refreshInterval.current = setInterval(doFetch, REFRESH_RATE.current);
  };

  return { data, loading, error, refetch };
}

function usePagedHook({
  fetchContent,
  processContent,
  args = [],
  refreshRate = null,
  keyField,
  initialState = [],
  vetoCondition = false,
  rowsCanChange = false,
  pauseAfterEmptyPages = null
}) {
  const REFRESH_RATE = useRef(refreshRate || null);
  const didCancel = useRef(false);
  const didCancelRefresh = useRef(false);
  const argsRef = useRef(args);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshEndIndex, setRefreshEndIndex] = useState(null); // (optional) last row to refresh to in "rowsCanChange" mode
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [loadingAllPages, setLoadingAllPages] = useState(false);
  const [error, setError] = useState(false);
  const [data, setData] = useState(initialState);
  const [pausedForEmptyPages, setPausedForEmptyPages] = useState(false); // Temp for orders until endpoints separated
  const [keyIndex, setKeyIndex] = useState([]); // allows for faster diffing when refreshing for new content
  const [nextPageUrl, setNextPageUrl] = useState(null);

  const generateKeyIndex = useCallback(
    newData => {
      if (!keyField) return [];
      return newData.map(datum => datum[keyField]);
    },
    [keyField]
  );

  const doPageFetch = useCallback(
    async (
      // eslint-disable-next-line default-param-last
      getNextPage = false,
      overridePagingState,
      resolveAllPages,
      rejectAllPages,
      recursedData = [...data],
      numPagesSkipped = 0
    ) => {
      try {
        if (vetoCondition && !resolveAllPages) {
          guardrail(() => {
            setData(initialState);
            setKeyIndex([]);
            setLoading(false);
            setLoadingNextPage(false);
            setLoadingAllPages(false);
          }, didCancel.current);
          return;
        }
        const argsAtStart = argsRef.current;
        // fetchContent must return a Promise
        const newNextPageUrl = getNextPage ? nextPageUrl : null;
        const returnedObject = await fetchContent(overridePagingState ?? newNextPageUrl);
        // wait for it to resolve
        const resolved = await returnedObject.results;
        if (!isNullish(resolved.nextPageUrl))
          guardrail(() => setNextPageUrl(resolved.nextPageUrl), didCancel.current);
        else if (getNextPage && isNullish(resolved.nextPageUrl))
          guardrail(() => setNextPageUrl(null), didCancel.current);
        const fetched = { ...returnedObject, results: resolved };
        const processed = await processContent(fetched);

        // If the args have changed since the function was called, abandon the update
        if (!arraysEqual(argsAtStart, argsRef.current)) {
          return;
        }

        // If getting data, but the page you got is empty
        // get another page (if one exists). Fixes the loading indicator 'flashing'
        if (isNullish(resolveAllPages) && !isNullish(resolved.nextPageUrl) && processed.length === 0) {
          // If we have to skip more than n empty pages,
          // pause and show the 'Get More' button
          if (!isNullish(pauseAfterEmptyPages) && numPagesSkipped >= pauseAfterEmptyPages) {
            guardrail(() => {
              setNextPageUrl(resolved.nextPageUrl);
              setPausedForEmptyPages(true);
              setLoading(false);
              setLoadingNextPage(false);
              if (!getNextPage) {
                setData([]);
                setKeyIndex([]);
              }
            }, didCancel.current);
            return;
          }
          doPageFetch(getNextPage, resolved.nextPageUrl, null, null, null, numPagesSkipped + 1);
          return;
        }

        // If getting the initial data
        if (!getNextPage && isNullish(resolveAllPages)) {
          guardrail(() => {
            setData(processed);
            setKeyIndex(generateKeyIndex(processed));
            setLoading(false);
          }, didCancel.current);
          return;
        }
        // If getting the next page of data and nothing else
        if (getNextPage && isNullish(resolveAllPages)) {
          guardrail(() => {
            setData([...data, ...processed]);
            setKeyIndex([...keyIndex, ...generateKeyIndex(processed)]);
            setLoadingNextPage(false);
            setLoading(false);
          }, didCancel.current);
          return;
        }
        // If getting all data, but there are more pages yet to fetch
        if (getNextPage && !isNullish(resolveAllPages) && !isNullish(resolved.nextPageUrl)) {
          guardrail(() => {
            setData([...recursedData, ...processed]);
            setKeyIndex([...keyIndex, ...generateKeyIndex(processed)]);
          }, didCancel.current);
          doPageFetch(true, resolved.nextPageUrl, resolveAllPages, rejectAllPages, [
            ...recursedData,
            ...processed
          ]);
          return;
        }
        // If getting all data and there are no more pages unfetched
        if (getNextPage && !isNullish(resolveAllPages) && isNullish(resolved.nextPageUrl)) {
          if (didCancel.current) return;
          guardrail(() => {
            setData([...recursedData, ...processed]);
            setKeyIndex([...keyIndex, ...generateKeyIndex(processed)]);
            setLoadingAllPages(false);
          }, didCancel.current);
          resolveAllPages([...recursedData, ...processed]);
        }
      } catch (caughtError) {
        console.error(caughtError); // eslint-disable-line
        guardrail(() => {
          setLoading(false);
          setLoadingNextPage(false);
          setLoadingAllPages(false);
        }, didCancel.current);
        if (rejectAllPages) rejectAllPages(caughtError);
        else {
          guardrail(() => setError(true), didCancel.current);
        }
      }
    },
    [
      data,
      keyIndex,
      fetchContent,
      processContent,
      vetoCondition,
      nextPageUrl,
      initialState,
      pauseAfterEmptyPages,
      generateKeyIndex
    ]
  );

  const doRefreshFetch = useCallback(
    async (thisPagingState = null, recursedNewData = []) => {
      try {
        if (vetoCondition) {
          guardrail(() => {
            setKeyIndex([]);
            setData(initialState);
            setLoading(false);
            setLoadingNextPage(false);
            setLoadingAllPages(false);
            setRefreshing(false);
          }, didCancelRefresh.current || didCancel.current);
          return;
        }

        let nextPagingState;
        const argsAtStart = argsRef.current;
        // either start at first page or use the pagingState provided in recursion
        const returnedObject = await fetchContent(thisPagingState);
        // wait for it to resolve
        const resolved = await returnedObject.results;
        if (resolved.nextPageUrl) nextPagingState = resolved.nextPageUrl;
        const fetched = { ...returnedObject, results: resolved };
        const processed = await processContent(fetched);

        // If the args have changed since the function was called, abandon the update
        if (!arraysEqual(argsAtStart, argsRef.current)) {
          return;
        }

        // If total amount of data < 1 page length, just replace and be done
        if (isNullish(thisPagingState) && isNullish(nextPagingState)) {
          guardrail(() => {
            setData(processed);
            setKeyIndex(generateKeyIndex(processed));
            setRefreshing(false);
          }, didCancelRefresh.current || didCancel.current);
          return;
        }

        let newData = [...recursedNewData, ...processed];

        // If exisiting data never changes, we can just prepend new entries when refreshing
        if (!rowsCanChange) {
          // Check which rows are new of the data we've fetched so far
          const difference = newData.filter(datum => !keyIndex.includes(datum[keyField]));
          // If all the data is new, keep fetching more until it isn't
          if (difference.length > 0 && difference.length === newData.length && !isNullish(nextPagingState)) {
            doRefreshFetch(nextPagingState, newData);
            return;
          }
          // Else insert just the actually new data, and we're done!
          newData = [...recursedNewData, ...difference];
          guardrail(() => {
            setData([...newData, ...data]);
            setKeyIndex([...generateKeyIndex(newData), ...keyIndex]);
            setRefreshing(false);
          }, didCancelRefresh.current || didCancel.current);
          return;
        }

        // If existing data can change, we need to refetch every page up until
        // the last data point we already fetched
        // and find if there are rows in our current data but NOT in our new data.
        const newDataKeyIndex = generateKeyIndex(newData);
        const reverseDifference = keyIndex
          // If the table provided an 'end' index, only check data up to that point
          .slice(0, refreshEndIndex ?? keyIndex.length)
          .filter(key => !newDataKeyIndex.includes(key));

        // If there are any such rows, we need to keep refreshing until there isn't
        if (reverseDifference.length > 0 && !isNullish(nextPagingState)) {
          doRefreshFetch(nextPagingState, newData);
          return;
        }

        // Else we've refreshed everything we need, or something has been deleted
        // and need to just overwrite the existing data
        guardrail(() => {
          setData(newData);
          setKeyIndex(newDataKeyIndex);
          // If table provided an end index, then we reset the pagingState to whatever it took
          // to refresh all data up to that index.
          if (refreshEndIndex !== null) setNextPageUrl(nextPagingState);
          setRefreshing(false);
        }, didCancelRefresh.current || didCancel.current);
      } catch (caughtError) {
        console.error(caughtError); // eslint-disable-line
        guardrail(() => {
          setError(true);
          setRefreshing(false);
        }, didCancelRefresh.current || didCancel.current);
      }
    },
    [
      data,
      keyIndex,
      fetchContent,
      processContent,
      vetoCondition,
      initialState,
      keyField,
      rowsCanChange,
      refreshEndIndex,
      generateKeyIndex
    ]
  );

  useEffect(() => {
    REFRESH_RATE.current = refreshRate;
  }, [refreshRate]);

  const refreshVeto =
    REFRESH_RATE.current === null ||
    !keyField ||
    error ||
    refreshing ||
    loading ||
    loadingNextPage ||
    loadingAllPages;

  // Sets up and tears down content refreshing timeout
  useEffect(() => {
    if (refreshVeto) return () => {};
    didCancelRefresh.current = false;
    const timer = setTimeout(() => {
      setRefreshing(true);
      doRefreshFetch();
    }, REFRESH_RATE.current);
    return () => {
      clearTimeout(timer);
    };
  }, [data, keyIndex, refreshVeto, rowsCanChange, refreshEndIndex, doRefreshFetch, REFRESH_RATE]);

  useEffect(
    () => {
      didCancel.current = false;
      argsRef.current = args;
      setError(false);
      setLoading(true);
      setNextPageUrl(null);
      setRefreshing(false);
      setLoadingNextPage(false);
      setLoadingAllPages(false);
      setPausedForEmptyPages(false);
      doPageFetch();
      return () => {
        didCancel.current = true;
        didCancelRefresh.current = true;
      };
    },
    [...args, vetoCondition, REFRESH_RATE] // eslint-disable-line
  );

  const refetch = useCallback(
    (setLoadingOnRefetch = true) => {
      didCancel.current = false;
      didCancelRefresh.current = true;
      setNextPageUrl(null);
      setError(false);
      setPausedForEmptyPages(false);
      setRefreshing(false);
      setLoadingNextPage(false);
      setLoadingAllPages(false);
      if (setLoadingOnRefetch) setLoading(true);
      doPageFetch();
    },
    [doPageFetch]
  );

  const refresh = useCallback(() => {
    if (refreshVeto) return;
    didCancel.current = false;
    didCancelRefresh.current = false;
    setRefreshing(true);
    doRefreshFetch();
  }, [refreshVeto, doRefreshFetch]);

  // Sets up and tears down content refreshing timeout
  useEffect(() => {
    if (refreshVeto) return () => {};
    didCancelRefresh.current = false;
    const timer = setTimeout(() => {
      setRefreshing(true);
      doRefreshFetch();
    }, REFRESH_RATE.current);
    return () => {
      clearTimeout(timer);
    };
  }, [data, keyIndex, refreshVeto, rowsCanChange, refreshEndIndex, doRefreshFetch, REFRESH_RATE]);

  const fetchNextPage = () => {
    if (loading || loadingNextPage || loadingAllPages || isNullish(nextPageUrl)) return;
    didCancelRefresh.current = true;
    setPausedForEmptyPages(false);
    setRefreshing(false);
    setLoadingNextPage(true);
    doPageFetch(true);
  };

  const fetchAllPages = useCallback(async () => {
    return new Promise((resolve, reject) => {
      try {
        didCancelRefresh.current = true;
        setRefreshing(false);
        setPausedForEmptyPages(false);
        if (isNullish(nextPageUrl)) resolve(data);
        setLoadingAllPages(true);
        doPageFetch(true, null, resolve, reject);
      } catch (caughtError) {
        setLoadingAllPages(false);
        reject(caughtError);
      }
    });
  }, [data, nextPageUrl, doPageFetch]);

  return {
    data,
    loading,
    refreshing,
    loadingNextPage,
    loadingAllPages,
    error,
    refetch,
    refresh,
    hasNextPage: nextPageUrl !== null,
    pausedForEmptyPages,
    fetchNextPage,
    fetchAllPages,
    setRefreshEndIndex
  };
}
